import { useSearchParams } from 'react-router-dom';
import { useConsentIdentityForm } from '../use-consent-identity-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { ControlledDateField, ControlledTextField } from '@act/storybook';
import React, { forwardRef, useImperativeHandle } from 'react';

interface ConsentIdentityFormProps {
  activeStep: number;
  setActiveStep: (step: number) => void;
  onFormValidityChange?: (isValid: boolean) => void;
}

export interface ConsentIdentityFormRef {
  submitForm: () => Promise<void>;
}

const ConsentIdentityForm = forwardRef<
  ConsentIdentityFormRef,
  ConsentIdentityFormProps
>(
  (
    { activeStep, setActiveStep: _setActiveStep, onFormValidityChange },
    ref
  ) => {
    const { t } = useTranslation();

    const [params] = useSearchParams();
    const key = params.get('key');

    const {
      formState: _formState,
      control,
      watch,
      onSubmit,
    } = useConsentIdentityForm({
      linkKey: key,
    });

    // Expose the onSubmit function to parent component
    useImperativeHandle(
      ref,
      () => ({
        submitForm: onSubmit,
      }),
      [onSubmit]
    );

    // Watch form values for real-time validation
    const watchedValues = watch();

    // Notify parent component about form validity changes
    React.useEffect(() => {
      if (onFormValidityChange) {
        // Only validate the current step's required fields
        let isValid = false;

        if (activeStep === 0) {
          // Step 0: Member Identity Confirmation - requires name and dateOfBirth
          const name = watchedValues.name;
          const dateOfBirth = watchedValues.dateOfBirth;
          isValid = Boolean(
            name && dateOfBirth && name.length >= 2 && dateOfBirth.length >= 8
          );
        } else if (activeStep === 1) {
          // Step 1: Address Confirmation - all fields are optional
          isValid = true;
        }

        onFormValidityChange(isValid);
      }
    }, [watchedValues, activeStep, onFormValidityChange]);

    const renderStepContent = () => {
      switch (activeStep) {
        case 0: // Member Identity Confirmation
          return (
            <div key="step-0">
              <div className="identity-form-row">
                <ControlledTextField
                  control={control}
                  label={
                    key ? t('consentForm.name') : t('consentForm.memberName')
                  }
                  name="name"
                  required
                />
              </div>
              <div className="identity-form-row">
                <ControlledTextField
                  control={control}
                  label={
                    key ? t('consentForm.email') : t('consentForm.memberEmail')
                  }
                  name="email"
                />
              </div>
              <div className="identity-form-row">
                <ControlledDateField
                  control={control}
                  inputType="date"
                  label={t('consentForm.dateOfBirth')}
                  name="dateOfBirth"
                  disablePast={false}
                  required
                />
              </div>
            </div>
          );

        case 1: // Address Confirmation
          return (
            <div key="step-1">
              <div className="identity-form-row">
                <ControlledTextField
                  control={control}
                  label={
                    key
                      ? t('consentForm.streetAddress')
                      : t('consentForm.memberStreetAddress')
                  }
                  name="line1"
                />
              </div>
              <div className="identity-form-row">
                <ControlledTextField
                  control={control}
                  label={t('consentForm.city')}
                  name="city"
                />
              </div>
              <div className="identity-form-row col-2">
                <ControlledTextField
                  control={control}
                  label={t('consentForm.state')}
                  name="state"
                />
                <ControlledTextField
                  control={control}
                  label={t('consentForm.zip')}
                  name="zip"
                />
              </div>
            </div>
          );

        default:
          return null;
      }
    };

    return <Box>{renderStepContent()}</Box>;
  }
);

export default ConsentIdentityForm;
